import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight, PlayCircle, FileDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useBrochureGate } from "@/components/BrochureGate";
import api from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const DEFAULT_SUB = "Saans Wellness offers an Ayurvedic & Nutraceutical PCD franchise built on authentic Ayurveda, modern science and our own WHO-GMP manufacturing — giving partners monopoly rights, high margins and complete marketing support.";

export default function Hero() {
  const { openGate } = useBrochureGate();
  const [cfg, setCfg] = useState({ hero_subtitle: "", cta_primary: "Apply for Franchise", cta_secondary: "View Products" });
  useEffect(() => {
    api.get("/settings").then(({ data }) => setCfg((c) => ({ ...c, ...data }))).catch(() => {});
  }, []);
  return (
    <section id="top" className="relative overflow-hidden bg-[#f9f8f5] pt-28 pb-16 md:pt-36 md:pb-24" data-testid="hero-section">
      {/* soft background accents */}
      <div className="pointer-events-none absolute -right-40 -top-32 h-[500px] w-[500px] rounded-full bg-[#f2ece4] blur-3xl" />
      <div className="pointer-events-none absolute -left-40 top-40 h-[400px] w-[400px] rounded-full bg-[#eef3ee] blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 md:px-8 lg:grid-cols-2">
        {/* Left copy */}
        <div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-[#e7e0d6] bg-white px-4 py-1.5 text-[#1f5d3a]">
            <ShieldCheck size={15} />
            <span className="eyebrow text-[#1f5d3a]">WHO-GMP · ISO Certified Manufacturing</span>
          </motion.div>

          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="mt-6 font-serif text-[2.7rem] leading-[1.05] tracking-tight text-[#202124] sm:text-6xl lg:text-[4.2rem]">
            India's next-generation <span className="italic text-[#d46a1d]">Ayurvedic &amp; Nutraceutical</span> PCD franchise.
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-6 max-w-xl text-lg leading-relaxed text-[#55534e]">
            {cfg.hero_subtitle && cfg.hero_subtitle.trim() ? cfg.hero_subtitle : DEFAULT_SUB}
          </motion.p>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-9 flex flex-col gap-3 sm:flex-row">
            <a href="#franchise" data-testid="hero-cta-franchise"
               className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#d46a1d] px-7 py-3.5 text-base font-semibold text-white transition-transform hover:scale-[1.03] hover:bg-[#c05f16]">
              {cfg.cta_primary || "Apply for Franchise"}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a href="#products" data-testid="hero-cta-products"
               className="inline-flex items-center justify-center gap-2 rounded-full border border-[#d8cfc1] bg-white px-7 py-3.5 text-base font-semibold text-[#202124] transition-colors hover:border-[#d46a1d] hover:text-[#d46a1d]">
              {cfg.cta_secondary || "View Products"}
            </a>
            <button onClick={() => openGate("brochure")} data-testid="hero-brochure"
               className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-3.5 text-base font-semibold text-[#1f5d3a] underline-offset-4 transition-colors hover:underline">
              <FileDown size={18} /> Download Brochure
            </button>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#6b6a65]">
            {["120+ Formulations", "Pan-India Distribution", "Free Marketing Kit", "24-hr Response"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-[#1f5d3a]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>

        {/* Right visual */}
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="relative">
          <div className="relative overflow-hidden rounded-[1.75rem] card-shadow-lg">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <motion.img
                initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 6, ease: "easeOut" }}
                src="https://images.unsplash.com/photo-1579165466949-3180a3d056d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Saans Wellness Ayurvedic and nutraceutical manufacturing laboratory"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 backdrop-blur">
              <PlayCircle size={16} className="text-[#d46a1d]" />
              <span className="text-xs font-semibold text-[#202124]">Inside our facility</span>
            </div>
          </div>

          {/* floating stat card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
            className="absolute -bottom-6 -left-4 rounded-2xl border border-[#e7e0d6] bg-white p-5 card-shadow md:-left-10">
            <div className="flex items-center gap-4">
              <div>
                <div className="font-serif text-3xl text-[#1f5d3a]">50,000+</div>
                <div className="text-xs font-medium text-[#6b6a65]">sq.ft. GMP facility</div>
              </div>
              <div className="h-10 w-px bg-[#e7e0d6]" />
              <div>
                <div className="font-serif text-3xl text-[#d46a1d]">6</div>
                <div className="text-xs font-medium text-[#6b6a65]">certifications</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
